import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import moment from "moment";
import { useMutation } from "@apollo/client";
import {
  MyTimeClosingPeriodNotApproved,
  MyTimeClosingPeriodNotApprovedIsMobile,
  MyTimeClosingPeriodNotApprovedWoExaminer,
  MyTimeClosingPeriodNotApprovedWoExaminerIsMobile,
  MyTimeClosingPeriodNotAuthorized,
  MyTimeClosingPeriodNotAuthorizedIsMobile,
  employeeMyTimeClosingPeriodRequest,
  employeeMyTimeClosingPeriodRequestIsMobile,
  employeeWithoutMyTimeExaminerRequest,
  employeeWithoutMyTimeExaminerRequestIsMobile,
} from "my-time/configs/table-defs";
import { MY_TIME_CLOSE_RECORDING_PERIOD } from "my-time/graphql/mutations";
import useActualDate from "my-time/hooks/use-actual-date/use-actual-date";
import { useMyTimeOpenApprovals } from "my-time/hooks/use-open-approvals";
import { useMyTimeOpenApprovalsWoExaminer } from "my-time/hooks/use-open-approvals-wo-examiner";
import { useMyTimeNotAuthorizedList } from "my-time/hooks/use-not-authorized-list";
import { useMyTimeNotAuthorizedWoExaminerList } from "my-time/hooks/use-not-authorized-wo-examiner-list";
import useMyTimeRequestsWithoutExaminer from "my-time/hooks/use-open-requests-without-examiner/use-open-requests-without-examiner";
import useOpenRequests from "my-time/hooks/use-open-requests/use-open-requests";
import useRecordingPeriod from "my-time/hooks/use-recording-period/use-recording-period";
import { myTimeMessageState } from "my-time/states/my-time-message";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState, useOcIsMobile, useOcSnackbar, useOcProperty } from "oc/hooks";

export const useMyTimeClosingPage = () => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const { date, formattedDate, handleDateChange } = useActualDate();
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const periodRange = {
    startDate: moment(date).startOf("month").toISOString(),
    endDate: moment(date).endOf("month").toISOString(),
  };

  const { data: userApprovalEnabledString } = useOcProperty({
    resource: "myTime",
    key: "userApprovalEnabled",
  });

  const userApprovalEnabled =
    userApprovalEnabledString === "true" ? true : false;

  const { data: examinerApprovalEnabledString } = useOcProperty({
    resource: "myTime",
    key: "examinerApprovalEnabled",
  });

  const examinerApprovalEnabled =
    examinerApprovalEnabledString === "true" ? true : false;

  const {
    data: openData,
    loading: openLoading,
    error: openError,
    refetch: openRefetch,
  } = useOpenRequests(periodRange);

  const {
    data: noExamData,
    loading: noExamLoading,
    error: noExamError,
    refetch: noExamRefetch,
  } = useMyTimeRequestsWithoutExaminer(periodRange);

  const {
    data: openApprovalsData,
    loading: openApprovalsLoading,
    error: openApprovalsError,
    refetch: openApprovalsRefetch,
  } = useMyTimeOpenApprovals();

  const {
    data: openApprovalsDataWoExaminer,
    loading: openApprovalsDataWoExaminerLoading,
    error: openApprovalsDataWoExaminerError,
    refetch: openApprovalsDataWoExaminerRefetch,
  } = useMyTimeOpenApprovalsWoExaminer();

  const {
    data: notAuthorizedData,
    loading: notAuthorizedLoading,
    error: notAuthorizedError,
    refetch: notAuthorizedRefetch,
  } = useMyTimeNotAuthorizedList();

  const {
    data: notAuthorizedWoExaminerData,
    loading: notAuthorizedWoExaminerLoading,
    error: notAuthorizedWoExaminerError,
    refetch: notAuthorizedWoExaminerRefetch,
  } = useMyTimeNotAuthorizedWoExaminerList();

  const tableInitialData = {
    ...initialTableData,
    sortField: "title",
    pageSize: 5,
  };
  const [openTableData, setOpenTableData] = useOcTableState(
    "employeeMyTimeClosingPeriodRequestData",
    tableInitialData,
  );
  const [noExamTableData, setNoExamTableData] = useOcTableState(
    "employeeWithoutMyTimeExaminerRequestData",
    tableInitialData,
  );

  const [noApprovedTableData, setNoApprovedTableData] = useOcTableState(
    "myTimeClosingPeriodNotApprovedData",
    initialTableData,
  );

  const [noApprovedWoExaminerTableData, setNoApprovedWoExaminerTableData] =
    useOcTableState(
      "myTimeClosingPeriodNotApprovedWoExaminersData",
      initialTableData,
    );

  const [notAuthorizedTableData, setNotAuthorizedTableData] = useOcTableState(
    "myTimeClosingPeriodNotAuthorizedData",
    initialTableData,
  );

  const [notAuthorizedWoExaminerTableData, setNotAuthorizedWoExaminerTableData] = useOcTableState(
    "myTimeClosingPeriodNotAuthorizedWoExaminerData",
    initialTableData,
  );

  const { setOpenMessage } = useOcSnackbar();
  const clearMessage = useSetRecoilState(myTimeMessageState);

  const [closeRecordingPeriodMutation] = useMutation(
    MY_TIME_CLOSE_RECORDING_PERIOD,
  );

  const closeRecordingPeriod = async () => {
    try {
      const { data } = await closeRecordingPeriodMutation();
      if (data?.closeRecordingPeriod?.result) {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_CLOSE_RECORDING_PERIOD_SUCCEEDED"),
        });
        clearMessage("");
        openRefetch();
      } else {
        setOpenMessage({
          type: "error",
          message:
            data?.closeRecordingPeriod?.message ||
            t("MY_TIME_CLOSE_RECORDING_PERIOD_FAILED"),
        });
        clearMessage("");
      }
    } catch {
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_CLOSE_RECORDING_PERIOD_FAILED"),
      });
    }
  };

  const handleCloseClick = () => setShowDialog(true);
  const handleConfirmClose = () => {
    setShowDialog(false);
    closeRecordingPeriod();
  };
  const handleCancelClose = () => setShowDialog(false);

  const handleTableChange =
    (setState: (state: any) => void, refetch: () => void) => (state: any) => {
      setState(state);
      if (
        state.searchField ||
        (state.searchText && state.searchText.length >= 3)
      ) {
        refetch();
      }
    };

  const handleSelectRow = (state: { _id: string }) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === state._id ? null : state._id);
    }
  };

  const filterRows = (rows: any[], tableState: any) => {
    if (!tableState.searchText || tableState.searchText.length < 3) return rows;
    const text = tableState.searchText.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((v) =>
        v?.toString().toLowerCase().includes(text),
      ),
    );
  };

  const effectiveOpenApprovals =
    userApprovalEnabled ? (openApprovalsData ?? []) : [];

  const effectiveOpenApprovalsWoExaminer =
    userApprovalEnabled ? (openApprovalsDataWoExaminer ?? []) : [];

  const effectiveNotAuthorized =
    examinerApprovalEnabled ? (notAuthorizedData ?? []) : [];

  const effectiveNotAuthorizedWoExaminer =
    examinerApprovalEnabled ? (notAuthorizedWoExaminerData ?? []) : [];

  const filteredOpenRows = filterRows(openData || [], openTableData);
  const filteredNoExamRows = filterRows(noExamData || [], noExamTableData);
  const filteredOpenApprovalsData = filterRows(effectiveOpenApprovals || [], noApprovedTableData);
  const filteredOpenApprovalsDataWoExaminer = filterRows(effectiveOpenApprovalsWoExaminer || [], noApprovedWoExaminerTableData);
  const filteredNotAuthorizedData = filterRows(effectiveNotAuthorized || [], notAuthorizedTableData);
  const filteredNotAuthorizedWoExaminerData = filterRows(effectiveNotAuthorizedWoExaminer || [], notAuthorizedWoExaminerTableData);

  const openRequestsProps = {
    tableDef: isMobile
      ? employeeMyTimeClosingPeriodRequestIsMobile
      : employeeMyTimeClosingPeriodRequest,
    filteredTableDef: {
      id: employeeMyTimeClosingPeriodRequest.id,
      columns: employeeMyTimeClosingPeriodRequest.columns.filter(
        (c) =>
          !employeeMyTimeClosingPeriodRequestIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "employeeMyTimeClosingPeriodRequestData",
    tableData: openTableData,
    setTableData: handleTableChange(setOpenTableData, openRefetch),
    onSelectRow: handleSelectRow,
    data: { rows: filteredOpenRows, total: filteredOpenRows.length },
    loading: openLoading,
    error: openError,
    selectedRowId,
  };

  const withoutExaminerProps = {
    tableDef: isMobile
      ? employeeWithoutMyTimeExaminerRequestIsMobile
      : employeeWithoutMyTimeExaminerRequest,
    filteredTableDef: {
      id: employeeWithoutMyTimeExaminerRequest.id,
      columns: employeeWithoutMyTimeExaminerRequest.columns.filter(
        (c) =>
          !employeeWithoutMyTimeExaminerRequestIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "employeeWithoutMyTimeExaminerRequestData",
    tableData: noExamTableData,
    setTableData: handleTableChange(setNoExamTableData, noExamRefetch),
    onSelectRow: handleSelectRow,
    data: { rows: filteredNoExamRows, total: filteredNoExamRows.length },
    loading: noExamLoading,
    error: noExamError,
    selectedRowId,
  };

  const openApprovalProps = {
    tableDef: isMobile
      ? MyTimeClosingPeriodNotApprovedIsMobile
      : MyTimeClosingPeriodNotApproved,
    filteredTableDef: {
      id: MyTimeClosingPeriodNotApproved.id,
      columns: MyTimeClosingPeriodNotApproved.columns.filter(
        (c) =>
          !MyTimeClosingPeriodNotApprovedIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "myTimeClosingPeriodNotApprovedData",
    tableData: noApprovedTableData,
    setTableData: handleTableChange(
      setNoApprovedTableData,
      openApprovalsRefetch,
    ),
    onSelectRow: handleSelectRow,
    data: {
      rows: filteredOpenApprovalsData,
      total: filteredOpenApprovalsData.length,
    },
    loading: openApprovalsLoading,
    error: openApprovalsError,
    selectedRowId,
  };

  const openApprovalWoExaminerProps = {
    tableDef: isMobile
      ? MyTimeClosingPeriodNotApprovedWoExaminerIsMobile
      : MyTimeClosingPeriodNotApprovedWoExaminer,
    filteredTableDef: {
      id: MyTimeClosingPeriodNotApprovedWoExaminer.id,
      columns: MyTimeClosingPeriodNotApprovedWoExaminer.columns.filter(
        (c) =>
          !MyTimeClosingPeriodNotApprovedWoExaminerIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "myTimeClosingPeriodNotApprovedWoExaminersData",
    tableData: noApprovedWoExaminerTableData,
    setTableData: handleTableChange(
      setNoApprovedWoExaminerTableData,
      openApprovalsDataWoExaminerRefetch,
    ),
    onSelectRow: handleSelectRow,
    data: {
      rows: filteredOpenApprovalsDataWoExaminer,
      total: filteredOpenApprovalsDataWoExaminer.length,
    },
    loading: openApprovalsDataWoExaminerLoading,
    error: openApprovalsDataWoExaminerError,
    selectedRowId,
  };

  const notAuthorizedProps = {
    tableDef: isMobile
      ? MyTimeClosingPeriodNotAuthorizedIsMobile
      : MyTimeClosingPeriodNotAuthorized,
    filteredTableDef: {
      id: MyTimeClosingPeriodNotAuthorized.id,
      columns: MyTimeClosingPeriodNotAuthorized.columns.filter(
        (c) =>
          !MyTimeClosingPeriodNotAuthorizedIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "myTimeClosingPeriodNotAuthorizedData",
    tableData: notAuthorizedTableData,
    setTableData: handleTableChange(
      setNotAuthorizedTableData,
      notAuthorizedRefetch,
    ),
    onSelectRow: handleSelectRow,
    data: {
      rows: filteredNotAuthorizedData,
      total: filteredNotAuthorizedData.length,
    },
    loading: notAuthorizedLoading,
    error: notAuthorizedError,
    selectedRowId,
  };

  const notAuthorizedWoExaminerProps = {
    tableDef: isMobile
      ? MyTimeClosingPeriodNotApprovedWoExaminerIsMobile
      : MyTimeClosingPeriodNotApprovedWoExaminer,
    filteredTableDef: {
      id: MyTimeClosingPeriodNotApprovedWoExaminer.id,
      columns: MyTimeClosingPeriodNotApprovedWoExaminer.columns.filter(
        (c) =>
          !MyTimeClosingPeriodNotApprovedWoExaminerIsMobile.columns.some(
            (m) => m.id === c.id,
          ),
      ),
    },
    tableId: "myTimeClosingPeriodNotAuthorizedWoExaminerData",
    tableData: notAuthorizedWoExaminerTableData,
    setTableData: handleTableChange(
      setNotAuthorizedWoExaminerTableData,
      notAuthorizedWoExaminerRefetch,
    ),
    onSelectRow: handleSelectRow,
    data: {
      rows: filteredNotAuthorizedWoExaminerData,
      total: filteredNotAuthorizedWoExaminerData.length,
    },
    loading: notAuthorizedWoExaminerLoading,
    error: notAuthorizedWoExaminerError,
    selectedRowId,
  };

  const { data: currentPeriod } = useRecordingPeriod();
  const today = moment().format("YYYY. MMMM");
  const disabled =
    openLoading ||
    noExamLoading ||
    openError ||
    noExamError ||
    (openData?.length ?? 0) > 0 ||
    (noExamData?.length ?? 0) > 0 ||
    effectiveOpenApprovals.length > 0 ||
    effectiveOpenApprovalsWoExaminer.length > 0 ||
    effectiveNotAuthorized.length > 0 ||
    effectiveNotAuthorizedWoExaminer.length > 0 ||
    currentPeriod === today ||
    moment(currentPeriod, "YYYY. MMMM").isAfter(moment(today, "YYYY. MMMM"));

  return {
    date,
    formattedDate,
    isMobile,
    showDialog,
    handleDateChange,
    handleCloseClick,
    handleConfirmClose,
    handleCancelClose,
    openRequestsProps,
    withoutExaminerProps,
    openApprovalProps,
    openApprovalWoExaminerProps,
    notAuthorizedProps,
    notAuthorizedWoExaminerProps,
    disabled,
  };
};

export default useMyTimeClosingPage;
